import { useState } from 'react'
import type { CaptainEvent, CaptainRole } from '../../types/captain'
import { ROLE_LABEL } from '../../types/captain'

interface Props {
  event: CaptainEvent
  /** Role the captain currently holds for this event, if any (drives "Change role" UX) */
  currentRole?: CaptainRole
  /** May be async — modal awaits it and locks the confirm button while in flight. */
  onConfirm: (role: CaptainRole) => void | Promise<unknown>
  onCancel: () => void
}

export default function RoleSelectModal({ event, currentRole, onConfirm, onCancel }: Props) {
  const [selected, setSelected] = useState<CaptainRole | null>(currentRole ?? null)
  const [submitting, setSubmitting] = useState(false)
  const isChange = currentRole !== undefined
  const titleId = `cp-modal-title-${event.id}`

  async function handleConfirmClick() {
    if (!selected || selected === currentRole || submitting) return
    setSubmitting(true)
    try {
      await onConfirm(selected)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="cp-modal-backdrop"
      onClick={(e) => {
        if (submitting) return
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="cp-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div
          style={{
            padding: '20px 22px 14px',
            borderBottom: '0.5px solid rgba(244,242,242,0.06)',
          }}
        >
          <div
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 10,
              color: '#C2E530',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            {isChange ? 'Change role' : 'Volunteer'}
          </div>
          <div
            id={titleId}
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontWeight: 700,
              fontSize: 19,
              color: '#F4F2F2',
              letterSpacing: '-0.01em',
            }}
          >
            {event.name}
          </div>
        </div>

        <div style={{ padding: '18px 22px' }}>
          <div
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'rgba(198,196,185,0.55)',
              marginBottom: 12,
            }}
          >
            Choose a role
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {event.slots.map((slot) => {
              const filled = slot.filledBy.length
              const isCurrent = slot.role === currentRole
              // ON-334: roles are a minimum target with unlimited overflow —
              // sign-ups are never rejected for a role being "full". The
              // only reason a role can't be picked is if it isn't being
              // recruited at all (target 0), which the backend also enforces
              // via `role_not_offered`. In practice `list_group_events_needing_captains`
              // already omits target-0 roles, so this rarely disables anything.
              const disabled = !isCurrent && slot.target <= 0
              const isSelected = selected === slot.role
              return (
                <button
                  key={slot.role}
                  onClick={() => !disabled && setSelected(slot.role)}
                  disabled={disabled}
                  data-testid={`role-option-${slot.role}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: isSelected ? 'rgba(194,229,48,0.08)' : 'transparent',
                    border: `0.5px solid ${
                      isSelected ? '#C2E530' : 'rgba(244,242,242,0.12)'
                    }`,
                    borderRadius: 8,
                    padding: '14px 16px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.35 : 1,
                    transition: 'all 140ms ease',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                    <span
                      style={{
                        fontFamily:
                          "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        color: '#F4F2F2',
                      }}
                    >
                      {ROLE_LABEL[slot.role]}
                    </span>
                    {isCurrent && (
                      <span
                        style={{
                          fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                          fontSize: 9,
                          color: '#EF606A',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                        }}
                      >
                        current
                      </span>
                    )}
                  </div>
                  {/* ON-334: participant-facing — show interest, never a target/denominator. */}
                  <span
                    style={{
                      fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                      fontSize: 12,
                      color: disabled ? 'rgba(198,196,185,0.4)' : '#C2E530',
                    }}
                  >
                    {filled > 0 ? `${filled} signed up` : 'Open'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px 20px',
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <button onClick={onCancel} className="cp-btn-ghost" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleConfirmClick}
            disabled={!selected || selected === currentRole || submitting}
            className="cp-btn-primary"
          >
            {submitting ? 'Saving…' : isChange ? 'Switch role' : 'Confirm sign-up'}
          </button>
        </div>
      </div>
    </div>
  )
}
